import React, { useEffect, useRef, useState } from 'react';
import { useOutsideClickHandler } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { SearchInput } from '../HierarchicalMultiSelect/search';
import Checkbox from '../Checkbox/Checkbox';

export type MultiSelectItem = {
  label: string;
  value: string;
  startDecorator?: React.ReactNode;
};

interface MultiSelectDropdownProps {
  name: string;
  options: MultiSelectItem[];
  className?: string;
  placeholder?: string;
  defaultValue?: string[];
  value?: string[];
  onChange?: (value: string[]) => void;
  wrapperClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  itemsPerPage?: number;
  isNotAll?: boolean;
  onlyAll?: boolean;
  labelClassName?: string;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  name,
  options,
  className,
  placeholder = 'Select Items',
  defaultValue = [],
  value,
  wrapperClassName,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  itemsPerPage = 20,
  isNotAll,
  onlyAll = false,
  labelClassName
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const firstLoadRef = useRef(true);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>(
    defaultValue ?? []
  );
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>(
    defaultValue ?? []
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ APPLY defaultValue ONLY on first mount
  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      if (defaultValue && defaultValue.length > 0) {
        setSelectedItems(defaultValue);
        setTempSelectedItems(defaultValue);
      }
    }
  }, [defaultValue]);

  // ✅ SYNC WITH CONTROLLED `value` FROM REDUX / PARENT
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedItems(value);
      setTempSelectedItems(value);
    }
  }, [value]);

  // FILTER + PAGINATE OPTIONS
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label?.toLowerCase().includes(searchTerm?.toLowerCase())
      )
    : options;

  const paginatedOptions = searchable
    ? filteredOptions.slice(0, currentPage * itemsPerPage)
    : filteredOptions;

  const hasMoreItems =
    searchable && filteredOptions.length > paginatedOptions.length;

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const loadMoreItems = () => setCurrentPage(prev => prev + 1);

  // CHECKBOX HANDLING
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (value === '__all__') {
      const visibleValues = paginatedOptions.map(item => item.value);

      if (checked) {
        const newSelected = [
          ...new Set([...tempSelectedItems, ...visibleValues])
        ];
        setTempSelectedItems(newSelected);
      } else {
        setTempSelectedItems(prev =>
          prev.filter(item => !visibleValues.includes(item))
        );
      }
      return;
    }

    setTempSelectedItems(prev =>
      prev.includes(value)
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const handleDone = () => {
    setSelectedItems(tempSelectedItems);
    if (onChange) {
      onChange(tempSelectedItems);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  useOutsideClickHandler(menuRef, () => {
    setIsOpen(false);
    setSearchTerm('');
    setCurrentPage(1);
  });

  return (
    <div
      className={`relative inline-block text-left ${wrapperClassName ?? ''}`}
      ref={menuRef}>
      <div>
        <Button
          onClick={toggleDropdown}
          type="button"
          btnStyle="link"
          className={`inline-flex justify-between w-full px-2 py-2 text-sm font-semibold text-[#505050] rounded-md hover:bg-gray-50 ${className ?? ''}`}>
          {`${placeholder}${selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}`}

          <svg
            className="w-5 h-5 ml-2 -mr-1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor">
            <path
              fillRule="evenodd"
              d="M6.293 9.293a1 1 0 011.414 0L10 11.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
        {/* <span className="text-xs text-[#505050]">{}</span> */}
      </div>

      {isOpen && (
        <div className="min-w-[215px] w-[215px] absolute z-[200] mt-2 py-3 origin-top-right bg-white rounded-md shadow-lg dropdown-height">
          {searchable && (
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={clearSearch}
              placeholder={searchPlaceholder}
              isFromMultiDropdown={true}
              className="px-3"
            />
          )}

          <div className="grid grid-cols-1 gap-1">
            {!isNotAll && paginatedOptions.length > 0 && (
              <label className="flex items-center px-2 pb-2">
                <Checkbox
                  value="__all__"
                  checked={
                    paginatedOptions.length > 0 &&
                    paginatedOptions.every(option =>
                      tempSelectedItems.includes(option?.value)
                    )
                  }
                  onChange={handleCheckboxChange}
                  name={name}
                  className="px-2 mr-3"
                />
                <span className="text-gray-900 text-sm capitalize ml-2">
                  {searchable && searchTerm ? 'Select All Visible' : 'All'}
                </span>
              </label>
            )}

            {!onlyAll && paginatedOptions.length > 0 ? (
              paginatedOptions.map(item => (
                <label key={item.value} className="flex items-center px-2 pb-2">
                  <Checkbox
                    value={item.value}
                    checked={tempSelectedItems.includes(item.value)}
                    onChange={handleCheckboxChange}
                    name={name}
                    className="px-2 mr-3"
                  />
                  <span>{item.startDecorator}</span>
                  <span
                    className={`text-gray-900 text-sm capitalize ml-2 ${labelClassName}`}>
                    {item?.label}
                  </span>
                </label>
              ))
            ) : !onlyAll ? ( // ✅ HIDE NO OPTIONS MESSAGE WHEN onlyAll
              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                {t('noOptionsFound')}
              </div>
            ) : null}

            {hasMoreItems &&
              !onlyAll && ( // ✅ HIDE LOAD MORE WHEN onlyAll
                <div className="px-2 pt-2 border-t border-gray-200 mt-2">
                  <button
                    onClick={loadMoreItems}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-200">
                    Load More (
                    {filteredOptions.length - paginatedOptions.length}{' '}
                    remaining)
                  </button>
                </div>
              )}
          </div>

          {(paginatedOptions.length > 0 || onlyAll) && ( // ✅ SHOW DONE BUTTON WHEN onlyAll
            <div className="w-full mt-2 flex px-3">
              <Button
                onClick={handleDone}
                btnStyle="primary"
                className="w-full rounded-lg px-2 py-2 font-semibold text-sm">
                {t('done')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
